import {
  ShieldCheck,
  Target,
  Rocket,
  Cpu,
  FlaskConical,
  Factory,
} from 'lucide-react';
import Slide from '@/components/shared/Slide';
import type { ExecutiveSummary, ViewMode } from '@/types/roadmap.types';

interface Props {
  executiveSummary: ExecutiveSummary;
  mode: ViewMode;
}

export default function FundingRealitySlide({ executiveSummary, mode }: Props) {
  const { funding_scenarios, cost_match, funding_disclaimer } = executiveSummary;

  const scenarioIcons = [ShieldCheck, Target, Rocket];
  const scenarioColors = [
    'text-blue-600 bg-blue-100',
    'text-emerald-600 bg-emerald-100',
    'text-amber-600 bg-amber-100',
  ];

  const costMatchIcons = [Cpu, FlaskConical, Factory];
  const costMatchColors = [
    'text-violet-600 bg-violet-100',
    'text-sky-600 bg-sky-100',
    'text-emerald-600 bg-emerald-100',
  ];

  return (
    <Slide mode={mode}>
      <div className="w-full mx-auto text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Executive Decision Summary
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-8 print:mb-3 print:text-[16pt]">
          Realistic Funding &amp; Cost Match
        </h2>

        {/* Funding scenarios — 3x1 */}
        <div className="relative mx-auto grid max-w-4xl divide-x divide-border border border-border sm:grid-cols-3 text-left mb-8 print:grid-cols-3 print:mb-3">
          {funding_scenarios.map((s, i) => {
            const Icon = scenarioIcons[i] ?? Target;
            const color = scenarioColors[i] ?? scenarioColors[0];
            return (
              <div key={s.label} className="space-y-1.5 p-5 print:p-2.5 print:space-y-1">
                <div className="flex items-center gap-2.5 print:gap-1.5">
                  <span className={`flex size-7 items-center justify-center rounded-full print:size-5 ${color}`}>
                    <Icon className="size-4 print:size-3" />
                  </span>
                  <h3 className="text-sm font-medium text-foreground print:text-[8pt]">
                    {s.label}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground print:text-[7.5pt] print:leading-snug">
                  {s.funding_range} across {s.targetable_programs} programs.
                  Own contribution: {s.co_funding_burden}.
                  Time to award: {s.time_to_award}.
                </p>
              </div>
            );
          })}
        </div>

        {funding_disclaimer && (
          <p className="text-[11px] text-muted-foreground/60 italic max-w-3xl mx-auto mb-6 print:text-[7pt] print:mb-2">
            {funding_disclaimer}
          </p>
        )}

        {/* Subheading */}
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 print:text-[7.5pt] print:mb-2">
          Cost Match by Investment Need
        </h3>

        {/* Cost match — 3x1 */}
        <div className="relative mx-auto grid max-w-4xl divide-x divide-border border border-border sm:grid-cols-3 text-left print:grid-cols-3">
          {cost_match.map((b, i) => {
            const Icon = costMatchIcons[i] ?? Cpu;
            const color = costMatchColors[i] ?? costMatchColors[0];
            const costs = Array.isArray(b.fundable_costs)
              ? b.fundable_costs
              : typeof b.fundable_costs === 'string'
                ? [b.fundable_costs]
                : [];
            const programs = Array.isArray(b.programs) ? b.programs : [];
            return (
              <div key={b.label} className="space-y-2 p-5 print:p-2.5 print:space-y-1">
                <div className="flex items-center gap-2.5 print:gap-1.5">
                  <span className={`flex size-7 items-center justify-center rounded-full print:size-5 ${color}`}>
                    <Icon className="size-4 print:size-3" />
                  </span>
                  <h3 className="text-sm font-medium text-foreground print:text-[8pt]">
                    {b.label}
                  </h3>
                </div>
                <ul className="space-y-0.5 print:space-y-0">
                  {costs.map((cost) => (
                    <li
                      key={cost}
                      className="text-sm leading-relaxed text-muted-foreground print:text-[7.5pt] print:leading-snug flex gap-1.5"
                    >
                      <span className="text-muted-foreground/50 shrink-0">·</span>
                      {cost}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </Slide>
  );
}
